import type { JSONSchema7 } from "json-schema";
import jp from "jsonpath";

// Separate keys into non-array and array types for navigation
export const getSchemaObjectProperties = (schema: JSONSchema7): string[] => {
  const properties: string[] = [];
  for (const key in schema.properties) {
    // only include subobject types
    const curProp = schema.properties[key] as JSONSchema7;
    if (curProp.type?.includes("array") || curProp.type?.includes("object")) {
      properties.push(key);
    }
  }
  return properties;
};

/**
 * Recursively initialize null array fields to empty arrays based on schema
 * and default non-nullable boolean fields to false
 * This ensures RJSF can properly add items to nested arrays
 */
export const initializeNullArrays = (
  data: any,
  schema: any,
  definitions: any
): any => {
  if (!data || !schema || typeof data !== "object") return data;

  const result = Array.isArray(data) ? [...data] : { ...data };

  // Get properties from schema, resolving $ref if needed
  let properties = schema.properties;
  if (schema.$ref && definitions) {
    const refName = schema.$ref.replace("#/definitions/", "");
    properties = definitions[refName]?.properties;
  }

  if (!properties) return result;

  for (const key of Object.keys(properties)) {
    const propSchema = properties[key];
    const isArray =
      propSchema.type === "array" ||
      (Array.isArray(propSchema.type) && propSchema.type.includes("array"));

    // Check if it's a non-nullable boolean (type is exactly "boolean", not ["boolean", "null"])
    const isNonNullableBoolean = propSchema.type === "boolean";

    if (isArray && (result[key] === null || result[key] === undefined)) {
      // Initialize null arrays to empty arrays
      result[key] = [];
    } else if (
      isNonNullableBoolean &&
      (result[key] === null || result[key] === undefined)
    ) {
      // Default non-nullable boolean fields to false
      result[key] = false;
    } else if (result[key] && typeof result[key] === "object") {
      // Recursively process nested objects/arrays
      if (Array.isArray(result[key])) {
        // For arrays, process each item
        const itemSchema = propSchema.items;
        result[key] = result[key].map((item: any) =>
          initializeNullArrays(item, itemSchema, definitions)
        );
      } else {
        // For objects, recurse
        result[key] = initializeNullArrays(
          result[key],
          propSchema,
          definitions
        );
      }
    }
  }

  return result;
};

export const getReportInformationProperties = (
  jsonSchema: JSONSchema7,
  formData: any
) => {
  // Properties that are handled in their own sections (arrays/objects)
  const NON_GENERAL_REPORT_INFO_PROPERTIES = [
    "Properties",
    "Victims",
    "Classifications",
    "Suspects",
    "OtherInvolved",
    "Vehicles",
    "Narratives",
    "Businesses",
  ];

  // Get all property paths, excluding the ones in NON_GENERAL_REPORT_INFO_PROPERTIES
  const allPaths = jp.paths(jsonSchema, "$.properties.*");
  const paths = allPaths.filter((path) => {
    const propertyName = path[path.length - 1];
    return !NON_GENERAL_REPORT_INFO_PROPERTIES.includes(
      propertyName as string
    );
  });

  let schema: Record<string, any> = {
    properties: {},
    definitions: jsonSchema.definitions,
  };

  // build schema object
  paths.forEach((jsonPath) => {
    const propertyName = jsonPath[jsonPath.length - 1];
    const queryPath = jsonPath.join(".");
    const item = jp.query(jsonSchema, queryPath);

    schema["properties"][propertyName] = item[0];
  });

  // Extract property names from paths (e.g., ['$', 'properties', 'incidentDate'] -> 'incidentDate')
  const propertyNames = paths.map((path) => path[path.length - 1]);

  // Query formData for each property
  let data: Record<string, any> = {};
  propertyNames.forEach((propName) => {
    const queryPath = `$.${propName}`;
    const value = jp.query(formData, queryPath);
    const propValue = value.length > 0 ? value[0] : null;

    // Default non-nullable boolean fields to false
    const propSchema = schema.properties[propName];
    const isNonNullableBoolean = propSchema?.type === "boolean";
    if (isNonNullableBoolean && propValue === null) {
      data[propName] = false;
    } else {
      data[propName] = propValue;
    }
  });

  return { schema, data };
};
